/* 412 Vault — size unit conversion toggle (US M / US W / EU / UK)
   Display-only conversion with PER-BRAND charts, verified against official
   brand size charts (like Laced does).

   IMPORTANT: raw variant sizes on this store are US sizes (stock starts at
   US 4), so every chart below is keyed by US mens size -> [UK, EU label].
   USM is therefore just the raw value re-formatted, not looked up; UK/EU/USW
   are derived from it. (An earlier version of this file assumed raw sizes
   were UK and keyed the charts that way — that silently mis-converted every
   size, including the default US M view, so it's been re-keyed here.)

   1. NIKE / JORDAN (default): NOT a flat offset — below UK 6 the US gap is
      half a size, from UK 6 it is a full size. Because Nike's own chart
      has UK 6 legitimately equal to BOTH US 6.5/EU 39 and US 7/EU 40,
      keying by US size (rather than UK) lets both exist as separate rows
      pointing at the same UK 6 — no ambiguity, unlike the old UK-keyed map.
      US W = US M + 1.5.
   2. ADIDAS / YEEZY sneakers: UK = US - 0.5, EU in thirds (US 8.5 = UK 8 =
      EU 42). US W = US M + 1.
   3. YEEZY SLIDES / FOAM RUNNER: their own chart — UK and US M are 1:1,
      whole sizes, own EU values (US 8 = UK 8 = EU 42, US 9 = UK 9 = EU 43).
      US W = US M + 1.
   4. NEW BALANCE: UK = US - 0.5 throughout (like adidas), but its own EU
      values. US W = US M + 1.5 (like Nike, NOT +1 like adidas/slides).
      US 13.5 does not exist on New Balance's own chart (jumps US 13 -> 14),
      so that size is intentionally left unconverted.

   Brand is auto-detected from vendor + product title (data-brand-source).
   Sizes not present in a chart are left unconverted rather than guessed. */
(() => {
  // US-keyed maps: us mens -> [UK, EU label]
  const CHARTS = {
    nike: {
      womens: 1.5,
      map: {
        3.5: [3, '35.5'], 4: [3.5, '36'], 4.5: [4, '36.5'], 5: [4.5, '37.5'],
        5.5: [5, '38'], 6: [5.5, '38.5'], 6.5: [6, '39'], 7: [6, '40'],
        7.5: [6.5, '40.5'], 8: [7, '41'], 8.5: [7.5, '42'], 9: [8, '42.5'],
        9.5: [8.5, '43'], 10: [9, '44'], 10.5: [9.5, '44.5'], 11: [10, '45'],
        11.5: [10.5, '45.5'], 12: [11, '46'], 12.5: [11.5, '47'], 13: [12, '47.5'],
        13.5: [12.5, '48'], 14: [13, '48.5'], 14.5: [13.5, '49'], 15: [14, '49.5']
      }
    },
    adidas: {
      womens: 1,
      map: {
        4: [3.5, '36'], 4.5: [4, '36 2/3'], 5: [4.5, '37 1/3'], 5.5: [5, '38'],
        6: [5.5, '38 2/3'], 6.5: [6, '39 1/3'], 7: [6.5, '40'], 7.5: [7, '40 2/3'],
        8: [7.5, '41 1/3'], 8.5: [8, '42'], 9: [8.5, '42 2/3'], 9.5: [9, '43 1/3'],
        10: [9.5, '44'], 10.5: [10, '44 2/3'], 11: [10.5, '45 1/3'], 11.5: [11, '46'],
        12: [11.5, '46 2/3'], 12.5: [12, '47 1/3'], 13: [12.5, '48'], 13.5: [13, '48 2/3'],
        14: [13.5, '49 1/3'], 14.5: [14, '50']
      }
    },
    slides: {
      womens: 1,
      map: {
        4: [4, '37'], 5: [5, '38'], 6: [6, '39'], 7: [7, '40.5'],
        8: [8, '42'], 9: [9, '43'], 10: [10, '44.5'], 11: [11, '46'],
        12: [12, '47'], 13: [13, '48.5'], 14: [14, '50']
      }
    },
    newbalance: {
      womens: 1.5,
      map: {
        4: [3.5, '36'], 4.5: [4, '37'], 5: [4.5, '37.5'], 5.5: [5, '38'],
        6: [5.5, '38.5'], 6.5: [6, '39.5'], 7: [6.5, '40'], 7.5: [7, '40.5'],
        8: [7.5, '41.5'], 8.5: [8, '42'], 9: [8.5, '42.5'], 9.5: [9, '43'],
        10: [9.5, '44'], 10.5: [10, '44.5'], 11: [10.5, '45'], 11.5: [11, '45.5'],
        12: [11.5, '46.5'], 12.5: [12, '47'], 13: [12.5, '47.5'],
        14: [13.5, '49'] /* US 13.5 does not exist on NB's own chart */
      }
    }
  };

  const UNITS = ['USM', 'USW', 'EU', 'UK'];
  const DEFAULT_UNIT = 'USM';
  const STORAGE_KEY = 'vault-size-unit-v2';

  // Some brands' own charts have a single UK size legitimately shared by two
  // US sizes (e.g. Nike: UK 6 = US 6.5/EU 39 AND US 7/EU 40). For charts
  // flagged disambiguateUK, append the EU size in parens when showing UK
  // for one of those shared sizes, the way StockX does, so it's unambiguous.
  Object.keys(CHARTS).forEach((key) => {
    const chart = CHARTS[key];
    const ukCounts = {};
    Object.values(chart.map).forEach((entry) => {
      ukCounts[entry[0]] = (ukCounts[entry[0]] || 0) + 1;
    });
    chart.ukCounts = ukCounts;
  });
  CHARTS.nike.disambiguateUK = true;

  const fmt = (n) => String(n);

  function detectChart(source) {
    const s = String(source || '').toLowerCase();
    if (s.indexOf('slide') !== -1 || s.indexOf('foam runner') !== -1 || s.indexOf('foam rnnr') !== -1) {
      return CHARTS.slides;
    }
    if (s.indexOf('yeezy') !== -1 || s.indexOf('adidas') !== -1) return CHARTS.adidas;
    if (s.indexOf('new balance') !== -1 || s.indexOf('newbalance') !== -1) return CHARTS.newbalance;
    return CHARTS.nike;
  }

  class SizeUnitToggle extends HTMLElement {
    connectedCallback() {
      this.buttons = Array.from(this.querySelectorAll('button[data-unit]'));
      this.fieldset = this.closest('.select__fieldset');
      if (!this.fieldset || !this.buttons.length) return;

      this.chart = detectChart(this.dataset.brandSource);

      let saved = DEFAULT_UNIT;
      try { saved = localStorage.getItem(STORAGE_KEY) || DEFAULT_UNIT; } catch (e) {}
      if (UNITS.indexOf(saved) === -1) saved = DEFAULT_UNIT;
      this.unit = saved;

      this.buttons.forEach((btn) => {
        btn.addEventListener('click', () => this.setUnit(btn.dataset.unit));
      });

      const toggleText = this.fieldset.querySelector('[data-popout-toggle-text]');
      if (toggleText) {
        this.observer = new MutationObserver(() => this.formatToggle());
        this.observer.observe(toggleText, { childList: true, characterData: true, subtree: true });
      }

      this.setUnit(this.unit);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    // Raw variant text is a US size (e.g. "9", "9.5"); extract it.
    parseSize(text) {
      const match = String(text).match(/(\d+(?:\.\d+)?)/);
      return match ? parseFloat(match[1]) : null;
    }

    convert(us) {
      if (us == null) return null;
      if (this.unit === 'USM') return 'US M ' + fmt(us);
      if (this.unit === 'USW') return 'US W ' + fmt(us + this.chart.womens);
      const entry = this.chart.map[us];
      if (!entry) return null; /* unknown size: leave label unconverted */
      if (this.unit === 'UK') {
        const shared = this.chart.disambiguateUK && this.chart.ukCounts[entry[0]] > 1;
        return 'UK ' + fmt(entry[0]) + (shared ? ' (EU ' + entry[1] + ')' : '');
      }
      if (this.unit === 'EU') return 'EU ' + entry[1];
      return null;
    }

    label(raw) {
      const us = this.parseSize(raw);
      const converted = this.convert(us);
      if (converted == null) return null;
      const str = String(raw);
      const suffix = str.includes(' - ') ? str.slice(str.indexOf(' - ')) : '';
      return converted + suffix;
    }

    setUnit(unit) {
      this.unit = unit;
      try { localStorage.setItem(STORAGE_KEY, unit); } catch (e) {}
      this.buttons.forEach((btn) => {
        const active = btn.dataset.unit === unit;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      this.formatOptions();
      this.formatToggle();
    }

    formatOptions() {
      this.fieldset.querySelectorAll('[data-popout-option]').forEach((anchor) => {
        const span = anchor.querySelector('span');
        if (!span) return;
        if (!span.dataset.raw) span.dataset.raw = span.textContent.trim();
        const next = this.label(span.dataset.raw);
        span.textContent = next != null ? next : span.dataset.raw;
      });
    }

    formatToggle() {
      const toggleText = this.fieldset.querySelector('[data-popout-toggle-text]');
      if (!toggleText) return;
      const current = toggleText.textContent.trim();
      const input = this.fieldset.querySelector('[data-popout-input]');
      const raw = input && input.value ? input.value : current;
      const suffix = current.includes(' - ') ? current.slice(current.indexOf(' - ')) : '';
      const us = this.parseSize(raw);
      const converted = this.convert(us);
      if (converted == null) return;
      const next = converted + suffix;
      if (toggleText.textContent !== next) toggleText.textContent = next;
    }
  }

  if (!customElements.get('size-unit-toggle')) {
    customElements.define('size-unit-toggle', SizeUnitToggle);
  }
})();